import { useCallback, useEffect, useRef, useState } from "react";

import { useAPI, useError, useInterval, useLoading } from "hooks";

const SHADOW_REFETCH_INTERVAL_MILLISECONDS = 8000;

/*
  Fetches a shipment's progress and keeps it live. Lives on the shipment page
  rather than inside the header's progress stepper so the Quotes section can
  show the same counts without a second fetch.
*/
export function useShipmentProgress(
  shipment: ShipperCondensedShipment | undefined,
) {
  const { api } = useAPI();
  const { loading, setLoading } = useLoading(false);
  const { error, setError } = useError();
  const [shipmentProgress, setShipmentProgress] =
    useState<ShipmentProgress | null>(null);
  const shipmentRef = useRef<undefined | ShipperCondensedShipment>();

  useEffect(() => {
    if (loading && shipment) {
      setError(null);
      api<ShipmentProgress>("get", `/shipments/${shipment.info.id}/progress`)
        .then((res) => {
          setError(null);
          setShipmentProgress(res.data);
        })
        .catch((e) => {
          setError(
            e?.response?.data?.message ??
              "Failed to book shipment progress. Please contact us if the issue persists.",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [setError, loading, api, setLoading, shipment]);

  /*
    Refetches shipment progress without using loading state variables such that
    the user does not see a loading spinner constantly
    (since this function is called on an interval)
  */
  const shadowFetchProgress = useCallback(() => {
    if (shipment) {
      api<ShipmentProgress>("get", `/shipments/${shipment.info.id}/progress`)
        .then((res) => {
          setError(null);
          setShipmentProgress(res.data);
        })
        .catch((e) => {
          setError(
            e?.response?.data?.message ??
              "Failed to book shipment progress. Please contact us if the issue persists.",
          );
        });
    }
  }, [api, setShipmentProgress, shipment, setError]);

  const refreshProgressOnNewShipment = useCallback(() => {
    if (shipment && shipment.shipment_status !== "BOOKING_CANCELLED") {
      shadowFetchProgress();
    }
  }, [shipment, shadowFetchProgress]);
  /*
    Run refetch fn on an interval to keep the shipment progress
    with live data in the first few minutes after a shipment posting.
  */
  useInterval(
    refreshProgressOnNewShipment,
    SHADOW_REFETCH_INTERVAL_MILLISECONDS,
  );

  useEffect(() => {
    if (shipment && shipmentRef.current?.info?.id !== shipment.info.id) {
      shipmentRef.current = shipment;
      setLoading(true);
    }
  }, [shipment, setLoading]);

  useEffect(() => {
    if (shipment && shipmentRef.current?.version !== shipment.version) {
      shipmentRef.current = shipment;
      setLoading(true);
    }
  }, [shipment, setLoading]);

  useEffect(() => {
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { shipmentProgress, error };
}
